import { rectsOverlap, tileAt, tileBlocks, type MapDef, type Rect } from './map';
import { T, TILE } from './tiles';

const LOW_TILES = new Set<string>([TILE.HEDGE, TILE.FENCE]);

/** The player's feet collider. */
export function feet(x: number, y: number): Rect {
  return { x: x - 5, y: y - 4, w: 10, h: 5 };
}

const CELL = 8;
type Pt = { x: number; y: number };

/**
 * Collision + navigation for one map: solid tiles, solid props, map bounds.
 * Pathfinding runs A* on an 8px grid and then shortcuts the result with
 * line-of-sight checks, so tap-to-walk goes around furniture naturally.
 */
export class Navigator {
  readonly cols: number;
  readonly rows: number;
  private walk: Uint8Array;

  constructor(
    readonly map: MapDef,
    private rects: readonly Rect[],
    /** Solid but knee-high things (bushes, flowerbeds...) you can jump over. */
    private low: readonly Rect[] = [],
  ) {
    this.cols = Math.floor((map.w * T) / CELL);
    this.rows = Math.floor((map.h * T) / CELL);
    this.walk = new Uint8Array(this.cols * this.rows);
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        this.walk[cy * this.cols + cx] = this.blocked(cx * CELL + CELL / 2, cy * CELL + CELL / 2 + 2) ? 0 : 1;
      }
    }
  }

  blocked(x: number, y: number): boolean {
    const W = this.map.w * T, H = this.map.h * T;
    if (x < 6 || x > W - 6 || y < 6 || y > H - 1) return true;
    const f = feet(x, y);
    const tx0 = Math.floor(f.x / T), tx1 = Math.floor((f.x + f.w - 0.01) / T);
    const ty0 = Math.floor(f.y / T), ty1 = Math.floor((f.y + f.h - 0.01) / T);
    for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) if (tileBlocks(this.map, tx, ty)) return true;
    return this.rects.some((r) => rectsOverlap(f, r));
  }

  /** Blocked only by things you can jump over (hedges, fences, low props). */
  lowOnly(x: number, y: number): boolean {
    const W = this.map.w * T, H = this.map.h * T;
    if (x < 6 || x > W - 6 || y < 6 || y > H - 1) return false;
    const f = feet(x, y);
    const tx0 = Math.floor(f.x / T), tx1 = Math.floor((f.x + f.w - 0.01) / T);
    const ty0 = Math.floor(f.y / T), ty1 = Math.floor((f.y + f.h - 0.01) / T);
    for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) {
      if (tileBlocks(this.map, tx, ty) && !LOW_TILES.has(tileAt(this.map, tx, ty))) return false;
    }
    return this.rects.every((r) => !rectsOverlap(f, r) || this.low.includes(r));
  }

  /** Where a jump from (x, y) along (dx, dy) lands, or null if it can't clear what's in between. */
  hopLanding(x: number, y: number, dx: number, dy: number, reach = 30): Pt | null {
    for (let d = reach; d >= 12; d -= 3) {
      const tx = x + dx * d, ty = y + dy * d;
      if (this.blocked(tx, ty)) continue;
      let ok = true;
      for (let k = 2; k < d && ok; k += 2) {
        const sx = x + dx * k, sy = y + dy * k;
        if (this.blocked(sx, sy) && !this.lowOnly(sx, sy)) ok = false;
      }
      if (ok) return { x: tx, y: ty };
    }
    return null;
  }

  /** Axis-separated sliding: glide along walls instead of sticking to them. */
  slide(x: number, y: number, dx: number, dy: number): Pt {
    let nx = x, ny = y;
    if (dx && !this.blocked(nx + dx, ny)) nx += dx;
    if (dy && !this.blocked(nx, ny + dy)) ny += dy;
    // Corner assist: nudge around a corner when moving straight into its edge.
    if (nx === x && ny === y && (dx || dy)) {
      for (const n of [3, -3, 6, -6]) {
        if (dx && !dy && !this.blocked(x + dx, y + n)) return { x, y: y + Math.sign(n) * Math.min(Math.abs(n), 1.2) };
        if (dy && !dx && !this.blocked(x + n, y + dy)) return { x: x + Math.sign(n) * Math.min(Math.abs(n), 1.2), y };
      }
    }
    return { x: nx, y: ny };
  }

  private ok(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.cols && cy < this.rows && this.walk[cy * this.cols + cx] === 1;
  }

  private cellOf(x: number, y: number): [number, number] {
    return [Math.max(0, Math.min(this.cols - 1, Math.floor(x / CELL))), Math.max(0, Math.min(this.rows - 1, Math.floor((y - 2) / CELL)))];
  }

  private centre(cx: number, cy: number): Pt {
    return { x: cx * CELL + CELL / 2, y: cy * CELL + CELL / 2 + 2 };
  }

  /** Nearest walkable cell to (cx, cy), searching outward. */
  private nearestOk(cx: number, cy: number, maxR = 8): [number, number] | null {
    if (this.ok(cx, cy)) return [cx, cy];
    for (let r = 1; r <= maxR; r++) {
      let best: [number, number] | null = null;
      let bestD = Infinity;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r || !this.ok(cx + dx, cy + dy)) continue;
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            best = [cx + dx, cy + dy];
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  /** True if the straight segment is walkable. */
  lineClear(a: Pt, b: Pt): boolean {
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.ceil(d / 3);
    for (let i = 1; i <= steps; i++) {
      const k = i / steps;
      if (this.blocked(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k)) return false;
    }
    return true;
  }

  /** Waypoints from (sx, sy) to near (tx, ty), or null if unreachable. */
  findPath(sx: number, sy: number, tx: number, ty: number): Pt[] | null {
    const goal = { x: tx, y: ty };
    if (!this.blocked(tx, ty) && this.lineClear({ x: sx, y: sy }, goal)) return [goal];
    const start = this.nearestOk(...this.cellOf(sx, sy), 3);
    const end = this.nearestOk(...this.cellOf(tx, ty));
    if (!start || !end) return null;
    const cols = this.cols;
    const idx = (x: number, y: number) => y * cols + x;
    const g = new Float32Array(this.cols * this.rows).fill(Infinity);
    const came = new Int32Array(this.cols * this.rows).fill(-1);
    const closed = new Uint8Array(this.cols * this.rows);
    const heap = new MinHeap();
    const h = (x: number, y: number) => {
      const dx = Math.abs(x - end[0]), dy = Math.abs(y - end[1]);
      return dx + dy + (Math.SQRT2 - 2) * Math.min(dx, dy);
    };
    const s = idx(start[0], start[1]), e = idx(end[0], end[1]);
    g[s] = 0;
    heap.push(s, h(start[0], start[1]));
    let found = false;
    let expanded = 0;
    while (heap.size && expanded < 40000) {
      const cur = heap.pop();
      if (cur === e) {
        found = true;
        break;
      }
      if (closed[cur]) continue;
      closed[cur] = 1;
      expanded++;
      const cx = cur % cols, cy = (cur - cx) / cols;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const nx = cx + dx, ny = cy + dy;
          if (!this.ok(nx, ny)) continue;
          if (dx && dy && (!this.ok(cx + dx, cy) || !this.ok(cx, cy + dy))) continue; // no corner cutting
          const ni = idx(nx, ny);
          const ng = g[cur] + (dx && dy ? Math.SQRT2 : 1);
          if (ng < g[ni]) {
            g[ni] = ng;
            came[ni] = cur;
            heap.push(ni, ng + h(nx, ny));
          }
        }
      }
    }
    if (!found) return null;
    const cells: Pt[] = [];
    for (let c = e; c !== -1; c = came[c]) cells.push(this.centre(c % cols, Math.floor(c / cols)));
    cells.reverse();
    if (!this.blocked(tx, ty) && this.lineClear(cells[cells.length - 1], goal)) cells.push(goal);
    // String-pulling: skip every waypoint we can see past.
    const out: Pt[] = [];
    let from: Pt = { x: sx, y: sy };
    let i = 0;
    while (i < cells.length) {
      let j = cells.length - 1;
      while (j > i && !this.lineClear(from, cells[j])) j--;
      out.push(cells[j]);
      from = cells[j];
      i = j + 1;
    }
    return out;
  }
}

/** Binary min-heap of (id, priority). */
class MinHeap {
  private ids: number[] = [];
  private pr: number[] = [];
  get size(): number {
    return this.ids.length;
  }
  push(id: number, p: number): void {
    const a = this.ids, b = this.pr;
    let i = a.length;
    a.push(id);
    b.push(p);
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (b[parent] <= p) break;
      a[i] = a[parent];
      b[i] = b[parent];
      i = parent;
    }
    a[i] = id;
    b[i] = p;
  }
  pop(): number {
    const a = this.ids, b = this.pr;
    const top = a[0];
    const lastId = a.pop()!, lastP = b.pop()!;
    if (a.length) {
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        let mp = lastP;
        if (l < a.length && b[l] < mp) {
          m = l;
          mp = b[l];
        }
        if (r < a.length && b[r] < mp) m = r;
        if (m === i) break;
        a[i] = a[m];
        b[i] = b[m];
        i = m;
      }
      a[i] = lastId;
      b[i] = lastP;
    }
    return top;
  }
}
